import type { SignalMessage, IceCandidateEntry } from './TrainingSignalMessage';
import {
  dispatchConnectionStartAnalytics,
  dispatchConnectionCloseAnalytics,
  dispatchWebSocketOpenAnalytics,
  dispatchWebSocketErrorAnalytics,
  dispatchWebSocketCloseAnalytics,
} from './trainingConnectionSavvyAnalytics';

import type { TrainingConnection } from 'features/training-forker/interfaces';
import type { SourceMediaTrack } from 'features/source-media-tracks/services';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';
import { generateSecureRandomHex } from 'features/training-forker/utils/';
import { envConfig } from 'features/environment/services/';
import type { AppDispatch } from 'features/app/store/store';

// We use a small 16:9 frame (160x90) for the stub video tracks:
// - 16:9 keeps a typical aspect ratio so remote layout logic that inspects aspect does not get a 1:1 edge case.
// - 160x90 is far smaller than common camera resolutions (e.g. 1280x720) but still avoids very tiny (e.g. 1x1) encoder edge paths.
// - Solid black at 1 fps results in negligible bandwidth; dropping to smaller dimensions would not yield a meaningful reduction.
const STUB_VIDEO_WIDTH = 160;
const STUB_VIDEO_HEIGHT = 90;
const STUB_VIDEO_FPS = 1;

let InstanceCount = 0;
let TotalPeerConnectionCount = 0;
let ActivePeerConnectionCount = 0;

// Represents a persistent entry in a map of local track senders, keyed by SourceMediaType. Each
// entry is persistent for the life of the connection
interface SenderEntry {
  transceiver: RTCRtpTransceiver;
  activeTrack: MediaStreamTrack; // currently attached (real or stub)
  stubTrack: MediaStreamTrack; // persistent stub used when real track missing
  isUsingStub: boolean;
  dispose?: () => void;
}

export function TrainingConnectionSavvyF(
  dispatch: AppDispatch,
  authToken?: string | null
): TrainingConnection {
  InstanceCount++;
  const SavvyWsUrl = envConfig?.trainingForker?.wsUrl?.trim() || '';

  const activeTracks = new Set<SourceMediaTrack>();
  const localSenderMap = new Map<SourceMediaTypes, SenderEntry>();

  let peerConnection: RTCPeerConnection | undefined;
  let socket: WebSocket | undefined;
  let relayId = '';

  const startConnection = async (
    tracks: SourceMediaTrack[],
    connectionRelayId?: string
  ): Promise<void> => {
    log(
      `Starting connection (Instance ${InstanceCount}, Total connection #${TotalPeerConnectionCount}, Active connections: ${ActivePeerConnectionCount})`
    );
    dispatchConnectionStartAnalytics(dispatch, {
      instanceNumber: InstanceCount,
      totalConnections: TotalPeerConnectionCount,
      activeConnections: ActivePeerConnectionCount,
    });
    setActiveTracks(tracks);

    log('Connecting to Savvy server...');
    log(`WebSocket URL: ${SavvyWsUrl}`);

    if (!SavvyWsUrl.length) {
      console.error(
        '[TrainingConnectionSavvy] No WebSocket URL defined in env.trainingForker.wsUrl. Connection will not be established.'
      );
      return;
    }

    const token = authToken ?? 'MOCKAUTH';
    relayId =
      connectionRelayId ?? 'mockSessionId-' + generateSecureRandomHex(3);
    try {
      socket = new WebSocket(SavvyWsUrl, ['authorization-jwt', token]);
      socket.onopen = (e) => {
        log('WebSocket opened:', e);
        try {
          dispatchWebSocketOpenAnalytics(dispatch, {
            relayId,
            wsUrl: SavvyWsUrl,
            eventType: e.type,
            eventTimestamp: e.timeStamp,
          });
        } catch (err) {
          console.warn(
            '[TrainingConnectionSavvy] Analytics dispatch failed:',
            err
          );
        }
        handleNewConnection();
      };
      socket.onmessage = (e) => handleIncomingMessage(e);
      socket.onerror = (e) => handleSocketError(e);
      socket.onclose = (e) => handleSocketClose(e);
    } catch (e) {
      log('Error connecting to Savvy server:', e);
    }
  };

  const closeConnection = () => {
    if (peerConnection) {
      peerConnection.close();
      peerConnection = undefined;
      ActivePeerConnectionCount--;
    }
    for (const entry of localSenderMap.values()) {
      entry?.dispose?.();
    }

    localSenderMap.clear();

    dispatchConnectionCloseAnalytics(dispatch, {
      instanceNumber: InstanceCount,
      totalConnections: TotalPeerConnectionCount,
      activeConnections: ActivePeerConnectionCount,
    });

    log('Connection closed');
  };

  const updateTracks = (sourceMediaTracks: SourceMediaTrack[]) => {
    if (!peerConnection) {
      return;
    }
    // Refresh our active track cache
    setActiveTracks(sourceMediaTracks);

    const newTracks = new Map<SourceMediaTypes, MediaStreamTrack>();
    for (const t of sourceMediaTracks) {
      newTracks.set(t.sourceMediaType, t.track);
    }

    for (const mediaType of localSenderMap.keys()) {
      replaceTrackForType(mediaType, newTracks.get(mediaType));
    }

    // After replacements, emit a status message so the backend (or debugging tools) can know
    // which media types are currently backed by real vs stub tracks.
    sendTracksUpdateMessage();
  };

  function log(message: string, ...args: any[]) {
    console.log(
      `[TrainingConnectionSavvy] relayId: ${relayId?.length > 0 ? relayId : 'not set'} ${message}`,
      ...args
    );
  }

  function createStubAudioTrack(): [MediaStreamTrack, () => void] {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    gain.gain.value = 0; // silence
    oscillator.connect(gain);

    const dest = ctx.createMediaStreamDestination();
    gain.connect(dest);

    oscillator.start();
    const track = dest.stream.getAudioTracks()[0];

    return [
      track,
      () => {
        try {
          oscillator.stop();
        } catch {}
        try {
          oscillator.disconnect();
          gain.disconnect();
        } catch {}
        ctx.close().catch(() => undefined);
      },
    ];
  }

  function createStubVideoTrack(): [MediaStreamTrack, () => void] {
    const canvas = document.createElement('canvas');
    canvas.width = STUB_VIDEO_WIDTH;
    canvas.height = STUB_VIDEO_HEIGHT;
    const ctx2d = canvas.getContext('2d');
    if (ctx2d) {
      ctx2d.fillStyle = 'black';
      ctx2d.fillRect(0, 0, canvas.width, canvas.height);
    }
    const interval = setInterval(() => {
      if (!ctx2d) {
        return;
      }
      ctx2d.fillStyle = 'black';
      ctx2d.fillRect(0, 0, canvas.width, canvas.height);
    }, 3000);
    const stream = canvas.captureStream(STUB_VIDEO_FPS);
    const track = stream.getVideoTracks()[0];
    return [
      track,
      () => {
        clearInterval(interval);
      },
    ];
  }

  function createStubTrackForType(
    type: SourceMediaTypes
  ): [MediaStreamTrack, () => void] {
    switch (type) {
      case SourceMediaTypes.HEARING_AUDIO:
      case SourceMediaTypes.INTERP_AUDIO:
      case SourceMediaTypes.DEAF_AUDIO:
        return createStubAudioTrack();
      case SourceMediaTypes.INTERP_VIDEO:
      case SourceMediaTypes.DEAF_VIDEO:
        return createStubVideoTrack();
      default:
        throw new Error(
          `Detected unknown SourceMediaType ${type} when creating stub track`
        );
    }
  }

  function setActiveTracks(tracks: SourceMediaTrack[]) {
    activeTracks.clear();

    for (const track of tracks) {
      activeTracks.add(track);
    }
  }

  function createIceCandidateEntry(
    candidate: RTCIceCandidate
  ): IceCandidateEntry {
    return {
      candidate: candidate.candidate ?? '',
      sdpMid: candidate.sdpMid ?? '',
      sdpMLineIndex: candidate.sdpMLineIndex ?? 0,
      usernameFragment: candidate.usernameFragment ?? '',
    };
  }

  function handleLocalIceCandidate(ev: RTCPeerConnectionIceEvent) {
    log('Local ICE candidate:', ev.candidate);

    if (ev.candidate) {
      sendSignalMessage({
        sessionId: relayId,
        type: 'candidate',
        iceCandidates: [createIceCandidateEntry(ev.candidate)],
      });
    } else {
      log('All local ICE candidates have been handled.');
    }
  }

  function handleIceConnectionStateChange(_: Event) {
    log('ICE connection state change: ', peerConnection?.iceConnectionState);
    log('WARNING: ICE connection state change not implemented');
  }

  function handleConnectionStateChange(_: Event) {
    log('Connection state change: ', peerConnection?.connectionState);
    log('WARNING: Connection state change not implemented');
  }

  function handleRemoteTrack(_: RTCTrackEvent) {
    log('Remote track');
    log('WARNING: Remote track not implemented');
  }

  async function handleNewConnection() {
    if (!socket) {
      throw new Error('Socket not initialized');
    }

    TotalPeerConnectionCount++;
    ActivePeerConnectionCount++;

    peerConnection = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    peerConnection.onicecandidate = (e) => handleLocalIceCandidate(e);
    peerConnection.oniceconnectionstatechange = (e) =>
      handleIceConnectionStateChange(e);
    peerConnection.onconnectionstatechange = (e) =>
      handleConnectionStateChange(e);
    peerConnection.ontrack = (e) => handleRemoteTrack(e);

    addInitialLocalSenders(peerConnection, activeTracks);
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    sendSignalMessage({
      sessionId: relayId,
      type: 'offer',
      sdp: offer.sdp ?? '',
      midMap: createMidMap(),
    });
  }

  function handleIncomingMessage(e: MessageEvent) {
    log('Received message:', e.data);

    const message = JSON.parse(e.data);
    handleSignalMessage(message);
  }

  async function handleSignalMessage(msg: SignalMessage) {
    switch (msg.type) {
      case 'answer':
        log('Received answer from Savvy server!!!');
        if (!peerConnection) {
          throw new Error('Peer connection not initialized for answer');
        }

        log('Setting remote description:', msg.sdp);
        await peerConnection.setRemoteDescription({
          type: 'answer',
          sdp: msg.sdp,
        });

        break;

      case 'candidate':
        log('Received ICE candidate from Savvy server.');
        if (!peerConnection) {
          throw new Error('Peer connection not initialized for ICE candidate');
        }

        // Handle each ICE candidate in the array
        for (const iceCandidate of msg.iceCandidates) {
          await peerConnection.addIceCandidate(
            new RTCIceCandidate({
              candidate: iceCandidate.candidate,
              sdpMid: iceCandidate.sdpMid,
              sdpMLineIndex: iceCandidate.sdpMLineIndex,
              usernameFragment: iceCandidate.usernameFragment,
            })
          );
        }
        break;

      case 'health':
        // ignore
        break;

      default:
        console.warn('Unknown signal event:', msg);
        break;
    }
  }

  function handleSocketError(e: Event) {
    log('Socket error:', e);
    if (socket) {
      try {
        dispatchWebSocketErrorAnalytics(dispatch, {
          relayId,
          wsUrl: socket.url,
          readyState: socket.readyState,
          eventType: e.type,
          eventTimestamp: e.timeStamp,
        });
      } catch (err) {
        console.warn(
          '[TrainingConnectionSavvy] Analytics dispatch failed:',
          err
        );
      }
    }
  }

  function handleSocketClose(e: CloseEvent) {
    try {
      dispatchWebSocketCloseAnalytics(dispatch, {
        relayId,
        wsUrl: socket?.url,
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        eventType: e.type,
        eventTimestamp: e.timeStamp,
        readyState: socket?.readyState,
      });
      log('Socket closed:', e);
    } catch (err) {
      console.warn('[TrainingConnectionSavvy] Analytics dispatch failed:', err);
    }
  }

  function addInitialLocalSenders(
    peer: RTCPeerConnection,
    providedTracks: Set<SourceMediaTrack>
  ) {
    const providedMap = new Map<SourceMediaTypes, MediaStreamTrack>(
      Array.from(providedTracks).map((t) => [t.sourceMediaType, t.track])
    );

    // We provision a transceiver (with a sender carrying either a real or stub track) for EVERY SourceMediaType up front.
    for (const mediaType of Object.values(SourceMediaTypes)) {
      const realTrack = providedMap.get(mediaType as SourceMediaTypes);
      const [stubTrack, disposeStubTrack] = createStubTrackForType(
        mediaType as SourceMediaTypes
      );
      const activeTrack = realTrack ?? stubTrack;
      const transceiver = peer.addTransceiver(activeTrack, {
        direction: 'sendonly',
      });

      localSenderMap.set(mediaType, {
        transceiver,
        activeTrack: activeTrack,
        stubTrack,
        isUsingStub: !realTrack,
        dispose: disposeStubTrack,
      });

      log(
        `Added initial sender for ${mediaType} (${realTrack ? 'real' : 'stub'})`
      );
    }
  }

  function replaceTrackForType(
    mediaType: SourceMediaTypes,
    newTrack: MediaStreamTrack | undefined
  ) {
    const entry = localSenderMap.get(mediaType);
    if (!entry) {
      log(`replaceTrackForType: no sender entry for ${mediaType}`);
      return;
    }
    const newActiveTrack = newTrack ?? entry.stubTrack;
    if (entry.activeTrack === newActiveTrack) {
      return;
    }

    log(
      `Replacing track for ${mediaType} with ${newTrack ? 'real' : 'stub'} track`
    );
    entry.transceiver.sender
      .replaceTrack(newActiveTrack)
      .catch((err) => log('replaceTrack error', err));
    entry.activeTrack = newActiveTrack;
    entry.isUsingStub = !newTrack;
  }

  function sendTracksUpdateMessage() {
    log('##### Preparing to send tracks status message #####');
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const tracksUpdateMsg: SignalMessage = {
      sessionId: relayId,
      type: 'tracks_update',
      midMap: createMidMap(),
    };
    sendSignalMessage(tracksUpdateMsg);
    log('Sent tracks-update message', tracksUpdateMsg);
  }

  function createMidMap(): Record<string, string> {
    const midMap: Record<string, string> = {};
    localSenderMap.forEach((entry, mediaType) => {
      const mid = entry?.transceiver?.mid;
      if (typeof mid !== 'string' || mid.length === 0) {
        log(
          `WARNING: Skipping midMap entry for ${mediaType} because transceiver.mid is not a valid string (value: ${mid})`
        );
        return;
      }
      midMap[mid] = mediaType;
    });

    return midMap;
  }

  function sendSignalMessage(message: SignalMessage) {
    if (!socket) {
      log('sendSignalMessage skipped (no socket)', message);
      return;
    }
    if (socket.readyState !== WebSocket.OPEN) {
      log(
        'sendSignalMessage skipped (socket not OPEN)',
        message,
        'state:',
        socket.readyState
      );
      return;
    }
    try {
      log('Sending signal message:', JSON.stringify(message));
      socket.send(JSON.stringify(message));
    } catch (e) {
      log('sendSignalMessage failed', e);
    }
  }

  return {
    startConnection: startConnection,
    closeConnection: closeConnection,
    updateTracks: updateTracks,
  };
}
